import React from 'react';
import { Box } from '@mui/material';
import { ContentSchema } from '../../../../definitions/schemas';
import ContactInformation, {
  ContactInformationProps,
} from '../../../Rooms/Content/Contact/ContactInformation';
import { EventProperty } from '../../../../tools/Analytics';
import { RoomContentContextProps } from '../../../../lib/RoomContentContext';
import RoomContentFacade, {
  RoomContentFacadeProps,
} from '../../../Rooms/RoomContentFacade';

type PickedContactInfoProps = Pick<
  ContactInformationProps,
  'buttonsStyles' | 'textStyles'
>;

type PickedFacadeProps = Pick<
  RoomContentFacadeProps,
  | 'classes'
  | 'contactId'
  | 'contentDescription'
  | 'contentName'
  | 'contentUrl'
  | 'disableImageContentName'
  | 'enableDocumentViewportDetection'
  | 'RoomEmbeddableContentProps'
  | 'roomId'
  | 'RoomTextContentProps'
  | 'structureContent'
  | 'track'
  | 'width'
>;

export interface RoomContentDetailsProps
  extends PickedContactInfoProps,
    PickedFacadeProps {
  actionable: boolean;
  content: Partial<ContentSchema>;
  contextProps: RoomContentContextProps;
  hideContactInformation?: boolean;
  isDownloadable?: boolean;
  referrer: string;
  trackingProperties?: EventProperty;
}

/**
 * Component that wraps the content viewer facade, used for rooms in their
 * "view" mode, including the information for contacting the person that
 * provided a content in references scenarios.
 */
const RoomContentDetails: React.FC<RoomContentDetailsProps> = (
  props: RoomContentDetailsProps
): JSX.Element => {
  const {
    actionable,
    buttonsStyles,
    classes,
    contactId,
    content,
    contentDescription,
    contentName,
    contentUrl,
    contextProps,
    disableImageContentName = false,
    enableDocumentViewportDetection = false,
    hideContactInformation = false,
    isDownloadable,
    referrer,
    RoomEmbeddableContentProps,
    roomId,
    RoomTextContentProps,
    structureContent,
    textStyles,
    track = false,
    trackingProperties,
    width,
  } = props;

  const id = content.id!;

  const renderContactInformation = () => {
    if (hideContactInformation || !content.contact) {
      return <></>;
    }
    return (
      <Box pt={2}>
        <ContactInformation
          actionable={actionable}
          buttonsStyles={buttonsStyles}
          contact={content.contact}
          contentId={id}
          roomId={roomId}
          textStyles={textStyles}
          trackingProperties={trackingProperties}
          userContactId={contactId}
        />
      </Box>
    );
  };

  return (
    <>
      <RoomContentFacade
        classes={classes}
        contactId={contactId}
        content={content}
        contentDescription={contentDescription}
        contentId={id}
        contentName={contentName}
        contentUrl={contentUrl}
        contextProps={contextProps}
        disableImageContentName={disableImageContentName}
        enableDocumentViewportDetection={enableDocumentViewportDetection}
        hasBorder
        isDownloadable={isDownloadable}
        referrer={referrer}
        RoomEmbeddableContentProps={RoomEmbeddableContentProps}
        roomId={roomId}
        RoomTextContentProps={RoomTextContentProps}
        rounded
        structureContent={structureContent}
        track={track}
        version={content.version}
        width={width}
      />
      {renderContactInformation()}
    </>
  );
};

export default RoomContentDetails;
